import { PayleoMark } from '@/components/logo/payleo';

import { Button, LinkButton } from '@repo/ui/components/button';
import { cn } from '@repo/ui/utils';
import * as Sentry from '@sentry/react-router';
import React from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router';

/**
 * - `page`: takes over the whole screen
 * - `section`: fills the part of the page that failed
 */
type ErrorLayout = 'page' | 'section';

/**
 * Errors intentionally thrown by page components / loaders
 * to be shown to the user as-is.
 *
 * @example
 * throw { code: 'no-payouts', title: 'No payouts', message: 'Create a payout first' } satisfies PageError;
 */
export type PageError = {
  code: string;
  title: string;
  message: string;
};

export function isPageError(error: unknown): error is PageError {
  return (
    typeof error === 'object' &&
    error !== null &&
    typeof (error as PageError).code === 'string' &&
    typeof (error as PageError).title === 'string' &&
    typeof (error as PageError).message === 'string'
  );
}

/** Reads the HTTP status from route responses and the various SDK errors */
function getStatus(error: unknown): number | undefined {
  if (isRouteErrorResponse(error)) return error.status;
  if (typeof error !== 'object' || error === null) return undefined;

  // `HTTPError` (@payleo/auth) uses `statusCode`, `WebError` / `EWAClientError` use `status`
  const { statusCode, status } = error as { statusCode?: unknown; status?: unknown };
  if (typeof statusCode === 'number') return statusCode;
  if (typeof status === 'number') return status;
  return undefined;
}

export function isForbiddenError(error: unknown) {
  return getStatus(error) === 403;
}

/** Service unreachable, timed out, aborted or temporarily unavailable */
export function isFetchError(error: unknown) {
  // matched by name, as there's more than one `FailedAfterRetryError` class in `@payleo/auth`
  if (error instanceof Error && error.name === 'FailedAfterRetryError') return true;

  // `fetch` rejects with a TypeError when the network request itself fails.
  // The message differs per browser (Chrome / Firefox / Safari)
  if (
    error instanceof TypeError &&
    /failed to fetch|networkerror|load failed|network request failed/i.test(error.message)
  ) {
    return true;
  }

  if (error instanceof DOMException && ['AbortError', 'TimeoutError'].includes(error.name)) {
    return true;
  }

  const status = getStatus(error);
  return status === 408 || status === 502 || status === 503 || status === 504;
}

// ---------------------------------------------------------------------------
// Views
// ---------------------------------------------------------------------------

function ErrorView({
  layout = 'page',
  eyebrow,
  title,
  message,
  actions,
  className,
}: {
  layout?: ErrorLayout;
  eyebrow?: string;
  title: string;
  message?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
}) {
  return (
    <main
      role="alert"
      data-layout={layout}
      className={cn(
        'flex w-full flex-col items-center justify-center px-6 text-center',
        layout === 'page'
          ? 'min-h-dvh py-24'
          : 'h-full min-h-48 rounded-xl border border-dashed py-10',
        className
      )}
    >
      {layout === 'page' && <PayleoMark size={8} className="mb-10" />}
      <div className="max-w-md space-y-3">
        {eyebrow && (
          <p className="text-payleo-orange-600 font-mono text-xs tracking-wide uppercase">
            {eyebrow}
          </p>
        )}
        <h1
          className={cn(
            'font-title text-foreground font-medium text-balance',
            layout === 'page' ? 'text-3xl' : 'text-xl'
          )}
        >
          {title}
        </h1>
        {message && <p className="text-muted-foreground text-sm text-pretty">{message}</p>}
      </div>
      {actions && <div className="mt-8 flex items-center gap-3">{actions}</div>}
    </main>
  );
}

const HomeAction = () => (
  <LinkButton href="/" variant="outline" design="rounded" size="sm">
    Go home
  </LinkButton>
);

const RefreshAction = () => (
  <Button variant="primary" design="rounded" size="sm" onClick={() => window.location.reload()}>
    Refresh page
  </Button>
);

/** The user doesn't have the permissions to view the contents */
export function ForbiddenError({ layout }: { layout?: ErrorLayout }) {
  return (
    <ErrorView
      layout={layout}
      eyebrow="403 · Forbidden"
      title="You don't have access to this"
      message="Your account doesn't have the permissions needed to view this. If you think this is a mistake, ask an owner or manager of your company."
      actions={layout === 'page' && <HomeAction />}
    />
  );
}

/** Failed to reach one of the services, or the request timed out */
export function FetchError({ layout }: { layout?: ErrorLayout }) {
  return (
    <ErrorView
      layout={layout}
      eyebrow="Connection problem"
      title="We couldn't load this"
      message="We're having trouble reaching our services. Check your connection and refresh the page."
      actions={<RefreshAction />}
    />
  );
}

/** Errors intentionally thrown by the page, shown as-is */
export function PageComponentError({ error, layout }: { error: PageError; layout?: ErrorLayout }) {
  return (
    <ErrorView
      layout={layout}
      eyebrow={error.code}
      title={error.title}
      message={error.message}
      actions={layout === 'page' && <HomeAction />}
    />
  );
}

/** Anything else, most likely a bug. Only the first line of the message is shown */
export function UnhandledError({ error, layout }: { error: unknown; layout?: ErrorLayout }) {
  React.useEffect(() => {
    console.error(error);
    Sentry.captureException(error);
  }, [error]);

  const raw = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  const firstLine = raw.split('\n')[0]?.trim();

  return (
    <ErrorView
      layout={layout}
      eyebrow="Unexpected error"
      title="Something went wrong"
      message={
        firstLine ? (
          <code className="bg-muted rounded px-1.5 py-0.5 font-mono text-xs break-all">
            {firstLine}
          </code>
        ) : (
          'An unexpected problem occurred on this page.'
        )
      }
      actions={
        <>
          {layout === 'page' && <HomeAction />}
          <RefreshAction />
        </>
      }
    />
  );
}

/**
 * Picks the right view for any thrown value.
 */
export function ErrorFallback({
  error,
  layout = 'page',
}: {
  error: unknown;
  layout?: ErrorLayout;
}) {
  if (isForbiddenError(error)) return <ForbiddenError layout={layout} />;
  if (isFetchError(error)) return <FetchError layout={layout} />;
  if (isPageError(error)) return <PageComponentError error={error} layout={layout} />;

  // other route responses, e.g. `throw data('...', { status: 404 })`
  if (isRouteErrorResponse(error)) {
    return (
      <PageComponentError
        layout={layout}
        error={{
          code: String(error.status),
          title: error.status === 404 ? 'Page not found' : error.statusText || 'Request failed',
          message:
            typeof error.data === 'string' && error.data
              ? error.data
              : error.status === 404
                ? "The page you're looking for doesn't exist or has been moved."
                : 'This request could not be completed.',
        }}
      />
    );
  }

  return <UnhandledError error={error} layout={layout} />;
}

/**
 * Use as a route's `ErrorBoundary` export.
 *
 * @example
 * export function ErrorBoundary() {
 *   return <RouteErrorBoundary />;
 * }
 */
export function RouteErrorBoundary() {
  return <ErrorFallback error={useRouteError()} layout="page" />;
}

/**
 * Contains errors thrown while rendering a subset of a page, so the rest
 * of the page remains usable.
 */
export class SectionErrorBoundary extends React.Component<
  { children: React.ReactNode; className?: string },
  { error: unknown; hasError: boolean }
> {
  state = { error: null as unknown, hasError: false };

  static getDerivedStateFromError(error: unknown) {
    return { error, hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className={this.props.className}>
          <ErrorFallback error={this.state.error} layout="section" />
        </div>
      );
    }
    return this.props.children;
  }
}
